import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  IconButton,
} from "@mui/material";
import "react-toastify/dist/ReactToastify.css"; // Make sure to import CSS
import { Check as CheckIcon, Clear as ClearIcon, Visibility, VisibilityOff } from "@mui/icons-material";
import ToastService from "@/service/toaster.service";
import { ToastContainer } from "react-toastify";
import vendorAuthService from "@/service/auth.service/vendor.auth.service";

const ChangePassword = () => {
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    number: false,
    specialCharacter: false,
  });
  const [strengthStatus, setStrengthStatus] = useState("Weak");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordsMatch, setPasswordsMatch] = useState(true);

  // States to control password visibility
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    // Check if passwords match whenever newPassword or confirmPassword change
    setPasswordsMatch(formData.newPassword === formData.confirmPassword);
  }, [formData.newPassword, formData.confirmPassword]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Validate password strength
    if (name === "newPassword" || name === "confirmPassword") {
      const lengthValid = value.length >= 8;
      const numberValid = /\d/.test(value);
      const specialCharValid = /[~!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/.test(value);

      setPasswordStrength({
        length: lengthValid,
        number: numberValid,
        specialCharacter: specialCharValid,
      });

      if (lengthValid && numberValid && specialCharValid) {
        setStrengthStatus("Strong");
      } else if (lengthValid && (numberValid || specialCharValid)) {
        setStrengthStatus("Moderate");
      } else {
        setStrengthStatus("Weak");
      }
    }
  };

  const handleSubmit = async () => {
    if (
      !passwordsMatch ||
      !passwordStrength.length ||
      !passwordStrength.number ||
      !passwordStrength.specialCharacter
    ) {
      // Don't submit if password conditions are not met
      alert("Please fix the issues before submitting.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await vendorAuthService.updatePassword(
        formData.currentPassword,
        formData.newPassword
      );
      if (response.status === true) {
        ToastService.success("Password Updated...");
        // Reset all states after successful password update
        setFormData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        setPasswordStrength({
          length: false,
          number: false,
          specialCharacter: false,
        });
        setStrengthStatus("Weak");
        setPasswordsMatch(true);
      } else {
        // alert("Error updating password.");
      }
    } catch (error) {
      console.error("Error:", error);
      ToastService.error(error.response.data.data.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isButtonDisabled =
    isSubmitting ||
    !passwordsMatch ||
    !passwordStrength.length ||
    !passwordStrength.number ||
    !passwordStrength.specialCharacter;

  return (
    <Box sx={{ maxWidth: 600, py: 3, p: 8 }}>
      <ToastContainer />
      <Typography variant="h6" gutterBottom>
        Change Your Password
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Ensure your account is using a strong password to protect your information.
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Current Password"
            type={showCurrentPassword ? "text" : "password"}  // Toggle password visibility
            variant="outlined"
            name="currentPassword"
            value={formData.currentPassword}
            onChange={handleChange}
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  edge="end"
                >
                  {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="New Password"
            type={showNewPassword ? "text" : "password"}  // Toggle password visibility
            variant="outlined"
            name="newPassword"
            value={formData.newPassword}
            onChange={handleChange}
            helperText="Password must be at least 8 characters and include a number and special character"
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  edge="end"
                >
                  {showNewPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Confirm New Password"
            type={showConfirmPassword ? "text" : "password"}  // Toggle password visibility
            variant="outlined"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            error={!passwordsMatch}
            helperText={passwordsMatch ? "" : "Passwords do not match"}
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Password Strength: <strong>{strengthStatus}</strong>
          </Typography>

          {/* Container for Password Requirements */}
          <Grid container spacing={1} direction="column" sx={{ mt: 2 }}>
            {/* At least 8 characters */}
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography
                  variant="body2"
                  color={passwordStrength.length ? "success.main" : "error.main"}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  {passwordStrength.length ? (
                    <CheckIcon sx={{ mr: 1, fontSize: 18 }} />
                  ) : (
                    <ClearIcon sx={{ mr: 1, fontSize: 18 }} />
                  )}
                  At least 8 characters.
                </Typography>
              </Box>
            </Grid>

            {/* Includes a number */}
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography
                  variant="body2"
                  color={passwordStrength.number ? "success.main" : "error.main"}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  {passwordStrength.number ? (
                    <CheckIcon sx={{ mr: 1, fontSize: 18 }} />
                  ) : (
                    <ClearIcon sx={{ mr: 1, fontSize: 18 }} />
                  )}
                  Includes a number (0-9).
                </Typography>
              </Box>
            </Grid>

            {/* Includes a special character */}
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography
                  variant="body2"
                  color={passwordStrength.specialCharacter ? "success.main" : "error.main"}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  {passwordStrength.specialCharacter ? (
                    <CheckIcon sx={{ mr: 1, fontSize: 18 }} />
                  ) : (
                    <ClearIcon sx={{ mr: 1, fontSize: 18 }} />
                  )}
                  Includes special characters (e.g. ~!@#$%^&).
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="contained"
            fullWidth
            sx={{
              backgroundColor: "#f59e0b",
              "&:hover": {
                backgroundColor: "#d97706",
              },
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
              color: "black", // This will make the text black
            }}
            onClick={handleSubmit}
            disabled={isButtonDisabled}
          >
            {isSubmitting ? "Updating..." : "Save New Password"}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ChangePassword;
